/**
 * chunk-reload — detekce stale lazy chunk failures (post-deploy) + auto-reload.
 *
 * Problém: po deploy Vite vygeneruje nové asset hashe (např. `LoginPage-abc.js`
 * → `LoginPage-xyz.js`). Uživatel který má v paměti starý index.html (nebo
 * navigates na route s React.lazy chunk) se pokouší fetchnout starý URL → 404.
 *
 * Browser chyba: `Failed to fetch dynamically imported module`.
 *
 * Řešení: detekuj signaturu chyby → `window.location.reload()`. Anti-loop guard
 * přes sessionStorage — pokud reload proběhl před < 10s, neopakuj (server může
 * být skutečně rozbitý a auto-reload by se zacyklil).
 */

import { RUNNING_BUILD_ID, fetchDeployedBuild } from "./version-tracking.js";

const RELOAD_FLAG_KEY = "bw:chunk-reload-at";
const ANTI_LOOP_WINDOW_MS = 10_000;
// Tvrdý strop: max N reloadů za okno. Chytí i POMALÉ smyčky (cyklus > 10s, který
// `ANTI_LOOP_WINDOW_MS` neošetří) — typicky rozbitý chunk trvale servírovaný z
// CacheFirst service workeru; reload ho neopraví, takže po N pokusech přestaneme.
const RELOAD_COUNT_KEY = "bw:chunk-reload-count";
const MAX_RELOADS = 3;
const RELOAD_COUNT_WINDOW_MS = 5 * 60_000;

/** Detekuje, zda je error způsoben failed dynamic import (stale chunk). */
export function isChunkLoadError(err: unknown): boolean {
  if (!err) return false;
  const message =
    err instanceof Error
      ? err.message
      : typeof err === "string"
        ? err
        : typeof (err as { message?: unknown }).message === "string"
          ? ((err as { message: string }).message)
          : "";
  if (!message) return false;
  return (
    message.includes("Failed to fetch dynamically imported module")
    || message.includes("Failed to load module script")
    || message.includes("error loading dynamically imported module")
    || message.includes("Importing a module script failed")
    // Chromium varianta po cache invalidation:
    || message.includes("dynamically imported module")
  );
}

/**
 * Reaguje na možný stale-chunk error. Vrací `true`, pokud chyba VYPADÁ jako selhání
 * dynamického importu (caller ji má potlačit — nezobrazovat crash UI), `false` jinak.
 *
 * DŮLEŽITÉ: samotné rozhodnutí, zda reloadovat, je ASYNCHRONNÍ a **gateované build-id**
 * (viz `scheduleChunkReloadDecision`). Reload se provede JEN když je na serveru reálně
 * nasazený NOVÝ build (stale chunk po deploy). Když běží AKTUÁLNÍ build, reload chybu
 * neopraví (rozbitá CacheFirst SW cache / transient fetch) a jen by se zacyklil → NEreloaduje.
 */
export function maybeReloadForChunkError(err: unknown): boolean {
  if (!isChunkLoadError(err)) return false;
  void scheduleChunkReloadDecision();
  return true; // je to chunk chyba → caller potlačí default (console spam / crash UI)
}

/** Kolik reloadů proběhlo v okně (a od kdy). Sdílené s anti-loop guardem. */
function reloadCount(now: number): { cnt: number; since: number } {
  try {
    const raw = sessionStorage.getItem(RELOAD_COUNT_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { n: number; since: number };
      if (now - parsed.since < RELOAD_COUNT_WINDOW_MS) return { cnt: parsed.n, since: parsed.since };
    }
  } catch {
    /* poškozený stav / private mode — reset */
  }
  return { cnt: 0, since: now };
}

/**
 * Asynchronně rozhodne o reloadu: (1) anti-loop < 10s, (2) tvrdý strop N reloadů/okno,
 * (3) **build-id gate** — reload jen když `/version.json` hlásí JINÝ build než běžící.
 * Bez skutečně nového deploye reload chybu neřeší a jen by se zacyklil.
 */
async function scheduleChunkReloadDecision(): Promise<void> {
  const now = Date.now();

  // (1) neopakuj reload pokud proběhl < 10s nazpět.
  try {
    const last = sessionStorage.getItem(RELOAD_FLAG_KEY);
    if (last) {
      const ageMs = now - Number.parseInt(last, 10);
      if (Number.isFinite(ageMs) && ageMs < ANTI_LOOP_WINDOW_MS) {
        console.warn("[chunk-reload] Reload proběhl nedávno (<10s) — nereloaduji znovu.");
        return;
      }
    }
  } catch {
    /* private mode — pokračuj */
  }

  // (2) tvrdý strop reloadů v okně.
  const { cnt, since } = reloadCount(now);
  if (cnt >= MAX_RELOADS) {
    console.warn(
      `[chunk-reload] Dosažen strop ${MAX_RELOADS} reloadů za ${RELOAD_COUNT_WINDOW_MS / 60000} min — ` +
        "chunk je nejspíš trvale rozbitý (CacheFirst SW cache). Přestávám reloadovat.",
    );
    return;
  }

  // (3) BUILD-ID GATE: reload má smysl JEN když je nasazený nový build. Na stejném buildu
  // (nebo když `/version.json` nedostupné) reload chybu neopraví → NEreloaduj (kill smyčky).
  const dep = await fetchDeployedBuild();
  const newBuildDeployed = !!dep && RUNNING_BUILD_ID !== "dev" && dep.buildId !== RUNNING_BUILD_ID;
  if (!newBuildDeployed) {
    console.warn(
      "[chunk-reload] Chunk selhal, ale běží AKTUÁLNÍ build (žádný nový deploy na /version.json) — " +
        "reload by nepomohl, NEreloaduji (prevence smyčky). Nejspíš rozbitá SW cache nebo transient fetch.",
    );
    return;
  }

  try {
    sessionStorage.setItem(RELOAD_COUNT_KEY, JSON.stringify({ n: cnt + 1, since }));
    sessionStorage.setItem(RELOAD_FLAG_KEY, String(now));
  } catch {
    /* private mode — reloadni i bez guard */
  }
  console.info(
    `[chunk-reload] Detekován NOVÝ build (${RUNNING_BUILD_ID} → ${dep!.buildId}) — reloaduji na aktuální verzi.`,
  );
  window.location.reload();
}

/**
 * Zaregistruje globální listenery pro `error` (script errors) a
 * `unhandledrejection` (Promise rejections z React.lazy). Volat 1× při bootstrap.
 */
export function installChunkReloadHandler(): void {
  window.addEventListener("error", (event) => {
    // ErrorEvent.error může být null (např. cross-origin script error)
    if (maybeReloadForChunkError(event.error ?? event.message)) {
      event.preventDefault();
    }
  });

  window.addEventListener("unhandledrejection", (event) => {
    if (maybeReloadForChunkError(event.reason)) {
      event.preventDefault();
    }
  });
}
